import React, { useEffect, useRef, useState } from "react";
import { ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { getChatHistory, sendChat } from "@/lib/api";
import { TYPE_ACCENT, TYPE_GLYPH } from "@/lib/format";

const SUGGESTIONS = [
  "Quanto ho speso questa settimana?",
  "Ce la faccio a fine mese?",
  "Dove sto sprecando soldi?",
  "Quando arriva il prossimo pagamento?",
];

const WELCOME = {
  id: "welcome",
  role: "assistant",
  text: "Ciao, sono il tuo CFO. Conosco ogni transazione del tuo conto: chiedimi quello che vuoi.",
  inline_cards: [],
};

const InlineCard = ({ c }) => {
  const accent = TYPE_ACCENT[c.type] || "#4D9CFF";
  return (
    <div
      className="mt-2 rounded-[8px] border border-white/[0.07] bg-[#050508] px-3 py-2"
      style={{ boxShadow: `0 0 0 1px ${accent}1f` }}
      data-testid="chat-inline-card"
    >
      <p className="caps-tag" style={{ color: accent, opacity: 0.8 }}>
        <span className="mr-1">{TYPE_GLYPH[c.type] || "\u25c6"}</span>
        {c.title}
      </p>
      {c.key_number && (
        <p className="mono mt-1 text-lg font-light tabular" style={{ color: accent }}>
          {c.key_number}
        </p>
      )}
    </div>
  );
};

// Core riusabile della Chat CFO: lista messaggi + suggerimenti + composer.
// - `active` controlla il caricamento della cronologia (modale: open; desktop docked: true).
// Usato sia da ChatSheet (modale mobile) sia dal rail destro desktop (HomeDesktop).
export const ChatPanel = ({ active = true, className = "" }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    getChatHistory()
      .then((h) => {
        if (!cancelled) setMessages(h.length ? h : [WELCOME]);
      })
      .catch(() => {
        if (!cancelled) setMessages([WELCOME]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [active]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, thinking]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || thinking) return;
    setInput("");
    setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: "user", text: msg, inline_cards: [] }]);
    setThinking(true);
    try {
      const res = await sendChat(msg);
      setMessages((prev) => [...prev, res]);
    } catch {
      toast.error("Il CFO non ha risposto: riprova");
    } finally {
      setThinking(false);
    }
  };

  const showChips = loaded && messages.filter((m) => m.role === "user").length === 0;

  return (
    <div className={`flex min-h-0 flex-1 flex-col ${className}`}>
      {/* messaggi - blocchi terminale */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4" data-testid="chat-messages">
        <div className="space-y-3">
          {messages.map((m) => (
            <div
              key={m.id}
              data-testid={m.role === "user" ? "chat-message-user" : "chat-message-assistant"}
              className={
                m.role === "user"
                  ? "border-l-2 border-[#4D9CFF] bg-[rgba(77,156,255,0.08)] py-2.5 pl-3 pr-3"
                  : "border-l-2 border-[#00FF87] bg-[#0D0D14] py-2.5 pl-3 pr-3"
              }
            >
              <p
                className="caps-tag mb-1"
                style={{ color: m.role === "user" ? "rgba(77,156,255,0.7)" : "rgba(0,255,135,0.7)" }}
              >
                {m.role === "user" ? "Tu" : "CFO"}
              </p>
              <p className="text-sm font-light leading-[1.55] text-white/85">{m.text}</p>
              {(m.inline_cards || []).map((c, i) => (
                <InlineCard key={c.id || c.fingerprint || `inline-${i}`} c={c} />
              ))}
            </div>
          ))}
          {thinking && (
            <div className="border-l-2 border-[#00FF87] bg-[#0D0D14] py-3 pl-3 pr-3" data-testid="chat-thinking">
              <div className="flex items-center gap-1.5">
                <span className="liqo-thinking-dot" />
                <span className="liqo-thinking-dot" />
                <span className="liqo-thinking-dot" />
                <span className="mono ml-2 text-[10px] tracking-[0.15em] text-white/35">ELABORO I NUMERI…</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* suggestion chips */}
      {showChips && !thinking && (
        <div className="flex flex-wrap gap-2 px-4 pb-3">
          {SUGGESTIONS.map((s, i) => (
            <button
              key={s}
              onClick={() => send(s)}
              data-testid={`chat-suggestion-chip-${i}`}
              className="rounded-[6px] border border-white/[0.12] bg-transparent px-3 py-2 text-[11px] font-light text-white/70 hover:bg-white/[0.05] active:scale-[0.98]"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* composer terminale */}
      <div className="border-t border-white/[0.06] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="flex items-center gap-2">
          <div className="flex h-11 flex-1 items-center gap-2 rounded-[8px] border border-white/10 bg-[#0D0D14] px-3">
            <span className="mono text-sm text-[#00FF87]">❯</span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Chiedi qualcosa..."
              data-testid="chat-message-input"
              className="mono h-full flex-1 bg-transparent text-sm font-light text-white placeholder:text-white/30 focus:outline-none"
            />
          </div>
          <button
            onClick={() => send()}
            disabled={!input.trim() || thinking}
            data-testid="chat-send-button"
            aria-label="Invia"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[8px] border border-[#00FF87]/40 bg-transparent text-[#00FF87] hover:bg-[#00FF87]/[0.08] active:scale-[0.95] disabled:opacity-30"
          >
            <ArrowUp size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
};
